import type { FC } from 'react';
import { Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/react/icons';
import styles from './geometry-sheet-header.module.scss';

export interface GeometrySheetHeaderProps {
  /** The well the sheet was opened for. */
  wellName: string;
  uwi14: string;
  /** A geometry edit is in flight. */
  saving: boolean;
  /** Hold Save for a reason other than saving — nothing changed yet, say. */
  saveDisabled?: boolean;
  /** Back to the Well Workspace, leaving any edit alone. Omitted, the bar has no
   *  arrow — the Wellbore Geometry Source modal reuses it that way. */
  onBack?: () => void;
  /** Drop the uncommitted edit and leave. */
  onCancel: () => void;
  /** Commit whatever is waiting and return to the Well Workspace. Point edits land
   *  as they are made, so with nothing waiting this is simply the way back. */
  onSave: () => void;
}

/**
 * The geometry tearsheet's own header: the wellbore it was opened for and the two
 * actions that close it, in place of the workspace's page chrome.
 * @param {GeometrySheetHeaderProps} props The wellbore, the save state and the actions.
 * @return {JSX.Element} The header.
 */
export const GeometrySheetHeader: FC<GeometrySheetHeaderProps> = ({
  wellName,
  uwi14,
  saving,
  saveDisabled = false,
  onBack,
  onCancel,
  onSave,
}) => (
  <header className={styles.header}>
    {onBack && (
      <Button
        kind="ghost"
        size="md"
        hasIconOnly
        renderIcon={ArrowLeft}
        iconDescription="Back to the well workspace"
        tooltipPosition="bottom"
        className={styles.back}
        onClick={onBack}
      />
    )}
    <div className={styles.titleBlock}>
      <h2 className={styles.title}>{wellName}</h2>
      <span className={styles.uwi}>UWI {uwi14}</span>
    </div>
    <div className={styles.actions}>
      <Button kind="secondary" size="md" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
      <Button
        kind="primary"
        size="md"
        onClick={onSave}
        disabled={saving || saveDisabled}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  </header>
);
